import React from 'react';
import {
  Box,
  Typography,
  Button,
  useTheme,
  Paper,
  CircularProgress
} from '@mui/material';
import { Check, Close } from '@mui/icons-material';
import { ApprovalRequest } from './types';

interface ApprovalRequestCardProps {
  approvalRequests: ApprovalRequest[];
  approvalStatus?: 'pending' | 'approved' | 'rejected';
  messageId: string;
  onApproval: (messageId: string, approved: boolean) => void;
  /** True while the approve/reject request is in progress */
  isApprovingLoading?: boolean;
}

const ApprovalRequestCard: React.FC<ApprovalRequestCardProps> = ({
  approvalRequests,
  approvalStatus,
  messageId,
  onApproval,
  isApprovingLoading = false
}) => {
  const theme = useTheme();

  const isCollapsed =
    approvalStatus === 'approved' || approvalStatus === 'rejected';

  const trimmedSummaries = approvalRequests
    .map((r) => r.display_message?.trim())
    .filter((s): s is string => Boolean(s));

  if (isCollapsed) {
    const label = approvalStatus === 'approved' ? 'Approved' : 'Rejected';
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'flex-start',
          gap: 0.5,
          maxWidth: '100%',
          color: approvalStatus === 'approved' ? 'success.main' : 'error.main',
          mt: 0.5
        }}
      >
        {approvalStatus === 'approved' ? (
          <Check sx={{ fontSize: 16, flexShrink: 0, mt: 0.125 }} />
        ) : (
          <Close sx={{ fontSize: 16, flexShrink: 0, mt: 0.125 }} />
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.125 }}>
          {trimmedSummaries.length <= 1 ? (
            <Typography
              variant="caption"
              sx={{
                fontStyle: 'italic',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
            >
              {label}
              {trimmedSummaries[0] ? (
                <>
                  {' — '}
                  <Box component="span" sx={{ fontStyle: 'normal' }}>
                    {trimmedSummaries[0]}
                  </Box>
                </>
              ) : null}
            </Typography>
          ) : (
            <>
              <Typography
                variant="caption"
                sx={{ fontStyle: 'italic', whiteSpace: 'pre-wrap' }}
              >
                {label}
              </Typography>
              {trimmedSummaries.map((summary, i) => (
                <Typography
                  key={i}
                  variant="caption"
                  sx={{
                    fontStyle: 'normal',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    pl: 0.5
                  }}
                >
                  {`${i + 1}. ${summary}`}
                </Typography>
              ))}
            </>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Paper
      elevation={2}
      sx={{
        maxWidth: '100%',
        p: 2,
        borderRadius: '8px',
        backgroundColor: theme.customColors?.themeTertiary || '#f5f5f5',
        border: `1px solid ${theme.customColors?.themePrimary || '#1976d2'}`,
        minWidth: '300px',
        marginBottom: 2
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        {approvalRequests.length > 1
          ? `Approvals required (${approvalRequests.length})`
          : 'Approval required'}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          mb: 2
        }}
      >
        {approvalRequests.map((req, i) => (
          <Typography
            key={i}
            variant="body2"
            sx={{ whiteSpace: 'pre-wrap' }}
          >
            {approvalRequests.length > 1 ? (
              <>
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {i + 1}.{' '}
                </Box>
                {req.display_message}
              </>
            ) : (
              req.display_message
            )}
          </Typography>
        ))}
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
          alignItems: 'center'
        }}
      >
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          onClick={() => onApproval(messageId, false)}
          disabled={isApprovingLoading}
          startIcon={
            isApprovingLoading ? undefined : <Close fontSize="small" />
          }
        >
          {isApprovingLoading ? 'Processing…' : 'Reject'}
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => onApproval(messageId, true)}
          disabled={isApprovingLoading}
          startIcon={
            isApprovingLoading ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <Check fontSize="small" />
            )
          }
        >
          {isApprovingLoading ? 'Processing…' : 'Approve'}
        </Button>
      </Box>
    </Paper>
  );
};

export default ApprovalRequestCard;
